import { useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Modal } from "../../../components/Modal";
import { Card } from "../../../components/Card";
import { Button } from "../../../components/Button";
import { Form } from "../../../components/Form";
import { FormActions } from "../../../components/FormActions";
import { FormGroup } from "../../../components/FormGroup";
import { PackageCard } from "../../../components/PackageCard";
import { InfoBox } from "../../../components/InfoBox";
import { PaymentTermsSection } from "./PaymentTermsSection";
import { spacing } from "@styling/sizing";
import colors from "@styling/colors";
import { BillingItemType } from "@KassaroAB/shared/types/enums/BillingItemType";
import { BillingPeriod } from "@KassaroAB/shared/types/enums/BillingPeriod";
import type { RestBillingItem } from "@KassaroAB/shared/types/billingItem";
import type { AvailableBillingItemId, StoreId } from "@KassaroAB/shared/types/ids";
import {
  useCancelBillingItemMutation,
  useSubscribeBillingItemMutation,
} from "../../../query/billingItemsQuery";
import { useGetStoreAvailableBillingItemsQuery } from "../../../query/availableBillingItemsQuery";
import { toDateString } from "../../../utils/dateUtils";
import { getQuantityOnDate } from "../../../utils/billingItemSegments";
import { useIsAdmin } from "../../../hooks/useIsAdmin";

const SectionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x4}px;
`;

const ItemGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: ${spacing.x2}px;
`;

const EmptyText = styled.p`
  margin: 0;
  color: ${colors.textMuted};
`;

interface ManageAddonsModalProps {
  isOpen: boolean;
  onClose: () => void;
  billingItems: RestBillingItem[] | undefined;
  defaultBillingPeriod?: BillingPeriod;
  defaultStartDate?: string;
}

export const ManageAddonsModal = ({
  isOpen,
  onClose,
  billingItems,
  defaultBillingPeriod = BillingPeriod.MONTHLY,
  defaultStartDate,
}: ManageAddonsModalProps) => {
  if (!isOpen) {
    return null;
  }

  return (
    <ManageAddonsModalContent
      onClose={onClose}
      billingItems={billingItems}
      defaultBillingPeriod={defaultBillingPeriod}
      defaultStartDate={defaultStartDate}
    />
  );
};

const ManageAddonsModalContent = ({
  onClose,
  billingItems,
  defaultBillingPeriod = BillingPeriod.MONTHLY,
  defaultStartDate,
}: Omit<ManageAddonsModalProps, "isOpen">) => {
  const { t } = useTranslation();
  const { storeId } = useParams<{ storeId: StoreId }>();
  const isAdmin = useIsAdmin();
  const today = toDateString(new Date());
  const activeAddonIds = (billingItems ?? [])
    .filter(
      (billingItem) =>
        billingItem.type === BillingItemType.ADD_ON &&
        getQuantityOnDate(
          [billingItem],
          billingItem.availableBillingItemId,
          today
        ) > 0
    )
    .map((billingItem) => billingItem.availableBillingItemId);
  const [selectedAddonIds, setSelectedAddonIds] =
    useState<AvailableBillingItemId[]>(activeAddonIds);
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>(
    defaultBillingPeriod
  );
  const [effectiveFrom, setEffectiveFrom] = useState(
    defaultStartDate ?? today
  );
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: availableBillingItems } = useGetStoreAvailableBillingItemsQuery(
    storeId ?? null
  );
  const { mutateAsync: subscribe, isPending: isSubscribing } =
    useSubscribeBillingItemMutation(storeId ?? null);
  const { mutateAsync: cancel, isPending: isCancelling } =
    useCancelBillingItemMutation(storeId ?? null);

  const addons = (availableBillingItems ?? []).filter(
    (item) => item.type === BillingItemType.ADD_ON
  );

  const toAdd = selectedAddonIds.filter(
    (addonId) => !activeAddonIds.includes(addonId)
  );
  const toRemove = activeAddonIds.filter(
    (addonId) => !selectedAddonIds.includes(addonId)
  );
  const hasChanges = toAdd.length > 0 || toRemove.length > 0;
  const isPending = isSubscribing || isCancelling;

  const toggleAddon = (addonId: AvailableBillingItemId) => {
    setSelectedAddonIds((current) =>
      current.includes(addonId)
        ? current.filter((id) => id !== addonId)
        : [...current, addonId]
    );
  };

  const handleClose = () => {
    setSubmitError(null);
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitError(null);

    const removeResults = await Promise.allSettled(
      toRemove.map((availableBillingItemId) =>
        cancel({
          availableBillingItemId,
          effectiveFrom,
        })
          .then(() => availableBillingItemId)
          .catch((error: Error) =>
            Promise.reject({ availableBillingItemId, error })
          )
      )
    );

    const addResults = await Promise.allSettled(
      toAdd.map((availableBillingItemId) =>
        subscribe({
          availableBillingItemId,
          quantity: 1,
          startDate: effectiveFrom,
          billingPeriod,
        })
          .then(() => availableBillingItemId)
          .catch((error: Error) =>
            Promise.reject({ availableBillingItemId, error })
          )
      )
    );

    const rejected = [...removeResults, ...addResults].filter(
      (result): result is PromiseRejectedResult => result.status === "rejected"
    );

    if (rejected.length > 0) {
      const names = rejected.map((result) => {
        const availableBillingItemId = (
          result.reason as { availableBillingItemId: AvailableBillingItemId }
        ).availableBillingItemId;
        const addon = addons.find((item) => item.id === availableBillingItemId);
        return addon?.name ?? availableBillingItemId;
      });
      setSubmitError(names.join(", "));
      return;
    }

    handleClose();
  };

  return (
    <Modal
      isOpen
      onClose={handleClose}
      title={t("packageSelection.addAddons")}
      maxWidth={900}
    >
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          void handleSubmit();
        }}
      >
        <SectionsContainer>
          <PaymentTermsSection
            paymentTerms={billingPeriod}
            onPaymentTermsChange={setBillingPeriod}
            startDate={effectiveFrom}
            onStartDateChange={setEffectiveFrom}
            minStartDate={isAdmin ? undefined : today}
          />

          {addons.length === 0 ? (
            <EmptyText>{t("billingItems.noAddonsAvailable")}</EmptyText>
          ) : (
            <FormGroup>
              <ItemGrid>
                {addons.map((item) => (
                  <PackageCard
                    key={item.id}
                    id="availableBillingItem"
                    name={item.name}
                    description={item.description}
                    price={item.pricing}
                    selected={selectedAddonIds.includes(item.id)}
                    onSelect={() => toggleAddon(item.id)}
                    selectionType="checkbox"
                    comingSoon={item.comingSoon ?? false}
                  />
                ))}
              </ItemGrid>
            </FormGroup>
          )}

          <Card>
            {submitError && (
              <InfoBox variant="error">
                {t("packageSelectionModal.saveFailed", {
                  message: submitError,
                })}
              </InfoBox>
            )}
            <FormActions>
              <Button
                type="button"
                $color="secondary"
                onClick={handleClose}
                disabled={isPending}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={isPending || !hasChanges}>
                {isPending
                  ? t("packageSelectionModal.saving")
                  : t("common.saveChanges")}
              </Button>
            </FormActions>
          </Card>
        </SectionsContainer>
      </Form>
    </Modal>
  );
};
